import { fetchLogisticsList } from '@/apis/order/order.api';
import { ILogisticsListRes, IOrderDetailReq, WareInfoListVo } from '@/apis/order/order.interface';
import UKDialog from '@/components/UKDialog';
import UKLoadingIcon, { CIRCLE_COLOR_MAP } from '@/components/UKLoadingIcon';
import UKToast from '@/components/UKToast/index';
import UKTruncatedTooltip from '@/components/UKTruncatedTooltip';
import { useTranslation } from '@/i18n/client';
import { DialogContent, DialogTitle } from '@mui/material';
import to from 'await-to-js';
import cn from 'clsx';
import { observer } from 'mobx-react';
import { useEffect, useRef, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import Stepper from '../stepper';
import styles from './index.module.scss';
// 图标
import VectorDown from '@/assets/icons/VectorDownBig.svg';
// 组件
// 埋点
import UKPageError from '@/components/UKPageError';
import { EXPOSURE_RECORD, sendClickTrack } from '@/tracks/25362';
import { eventTrackingUtils } from '@/utils/eventTrack/eventTrackingUtils';

interface trackModalProps {
  title: string;
  orderId: string;
  isOpen: boolean;
  closeHandler: () => void;
  commonTrackParams: {
    orderId: string;
    orderStatus: '1' | '3' | '8' | '6' | '5';
  };
}

const trackModal: React.FC<trackModalProps> = observer(
  ({ title, orderId, isOpen, closeHandler, commonTrackParams }) => {
    const { t } = useTranslation();
    const [logisticsList, setLogisticsList] = useState<ILogisticsListRes | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [curIndex, setCurIndex] = useState<number>(0);
    const [showFoldBtn, setShowFoldBtn] = useState<boolean>(false);
    const [openStates, setOpenStates] = useState<boolean>(false);

    const packageWrapRef = useRef<HTMLDivElement>(null);

    const loadLogisticslList = async () => {
      const requestData: IOrderDetailReq = { orderId: String(orderId) };
      const [err, logisticsListData] = await to(fetchLogisticsList(requestData));

      if (err) {
        setError('Failed to fetch order details');
        setLogisticsList(null);
      } else {
        setLogisticsList(logisticsListData as ILogisticsListRes);
        // 弹框曝光埋点
        eventTrackingUtils.submitExp({
          event_id: EXPOSURE_RECORD.OrderFollow_LayerExpo,
          json_param: {
            ...commonTrackParams,
            packageNum: logisticsListData?.multiPackageList?.length || 0,
            duration: 300,
          },
        });
        checkPackageListOverflow();
        setError(null);
      }

      setLoading(false);
    };

    useEffect(() => {
      loadLogisticslList();
    }, []);
    const checkPackageListOverflow = () => {
      setTimeout(() => {
        const containerElement = packageWrapRef.current;
        if (containerElement) {
          const packageWrapHeight = containerElement.clientHeight;
          if (packageWrapHeight > 44) {
            setShowFoldBtn(true);
          } else {
            setShowFoldBtn(false);
          }
        }
      }, 30);
    };

    const { addressInfoDTO, multiPackageList = [] } = logisticsList || {};
    const {
      addressTitle,
      address,
      customerName,
      mobile,
      isSelfPickupOrder,
      selfPickupName,
      selfPickupAddress,
    } = addressInfoDTO || {};
    const {
      promiseTitle,
      promiseValue,
      carrierCodeTitle,
      carrierCode,
      waybillNumTitle,
      waybillNum,
      packageLogistics,
      selectedPromiseTitle,
      selectedPromiseValue,
    } = multiPackageList[curIndex] || {};

    // 埋点需要数据
    const packageNum = `${multiPackageList?.length || 0}`;

    // 复制物流单号
    const copyHandler = () => {
      // 埋点
      sendClickTrack.OrderFollow_TrackIdCopy({
        ...commonTrackParams,
        packageNum,
      });
      UKToast.show({
        content: t('order.orderDetail.common.copySuccessTips'), // 弹窗内容
        icon: 'success', // 可选：success, fail, loading, warn, info，影响图标
        duration: 2000, // 不写的话默认3000
      });
    };

    // 计算package items
    const calcNum = (list: WareInfoListVo[]) => {
      return list.reduce((sum: number, item) => sum + Number(item.buyCount), 0);
    };

    return (
      <UKDialog
        open={isOpen}
        onClose={() => {
          // 埋点
          sendClickTrack.OrderFollow_Close({
            ...commonTrackParams,
            packageNum,
          });
          closeHandler();
        }}
        showCloseBtn={true}
        className={styles.repayOrderModal}
        sx={{
          '& .MuiDialog-paper': {
            width: '680px', // 设置自定义宽度
            height: '76vh',
          },
        }}
      >
        <DialogTitle>{t('order.orderDetail.trackModal.modalTitle')}</DialogTitle>
        <DialogContent>
          {loading ? (
            <div className={styles.loading_wrap}>
              <UKLoadingIcon type={CIRCLE_COLOR_MAP.BLACK} />
              <span className={styles.loading_text}>{t('common.pageStatus.loading')}</span>
            </div>
          ) : error ? ( // 异常兜底处理
            <div className={styles.errorWrap}>
              <UKPageError
                style={{ padding: '60px 0' }}
                textStyle={{ width: '100%' }}
                text={t('order.orderList.errorPage.tip')}
                btnText={t('order.orderList.errorPage.btnTip')}
                clickTryAgain={() => {
                  window.location.reload();
                }}
              />
            </div>
          ) : (
            <div className={styles.contentWrap}>
              {/* 一单多包裹场景 */}
              {multiPackageList?.length > 1 ? (
                <div
                  className={cn(styles.packageOuterWrap, {
                    [styles.oneLineStyle]: showFoldBtn && !openStates,
                  })}
                >
                  <div className={styles.packageWrap} ref={packageWrapRef}>
                    {/* 1 Item */}
                    {multiPackageList.map((item, index) => {
                      return (
                        <div
                          key={index}
                          className={cn(styles.packageItem, {
                            [styles.activePackageItem]: index === curIndex,
                          })}
                          onClick={() => {
                            sendClickTrack.OrderTrailFollow_Package({
                              ...commonTrackParams,
                              packageNum,
                              index,
                            });
                            setCurIndex(index);
                          }}
                          exptag={'exp|' + EXPOSURE_RECORD.OrderTrailFollow_PackageExpo}
                          data-exptag-config={JSON.stringify({
                            useClick: false,
                            stay_duration: 0.3,
                            repeated: false,
                            area_rate: 0.5,
                          })}
                          data-exptag-json-param={JSON.stringify({
                            ...commonTrackParams,
                            packageNum,
                            index,
                          })}
                        >
                          {t('order.orderDetail.orderDetailStatus.packageText')} {index + 1}
                          {calcNum(item?.skuList) ? (
                            <span>
                              ({calcNum(item?.skuList)}{' '}
                              {t('order.orderDetail.orderDetailStatus.item', {
                                count: calcNum(item?.skuList),
                              })}
                              )
                            </span>
                          ) : (
                            <span>({t('order.orderDetail.orderDetailStatus.noNumberText')})</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {showFoldBtn && (
                    <div
                      className={styles.viewAllWrap}
                      onClick={() => {
                        setOpenStates((pre) => !pre);
                      }}
                    >
                      {openStates
                        ? t('order.orderList.orderItems.retractText')
                        : t('checkout.orderCard.viewAll')}
                      <VectorDown
                        className={cn(styles.more_ico, {
                          [styles.retract_ico]: openStates,
                        })}
                      />
                    </div>
                  )}
                </div>
              ) : null}
              <ul className={styles.express_content}>
                {carrierCodeTitle ? (
                  <li className={styles.express_item}>
                    <span>{carrierCodeTitle}</span>
                    <span className={cn({ [styles.errorText]: !carrierCode })}>
                      {carrierCode || t('order.orderDetail.trackModal.noDataText')}
                    </span>
                  </li>
                ) : null}
                {waybillNumTitle ? (
                  <li className={styles.express_item}>
                    <span>{waybillNumTitle}</span>
                    {waybillNum ? (
                      <div>
                        <span>{waybillNum}</span>
                        <CopyToClipboard text={waybillNum || ''} onCopy={() => copyHandler()}>
                          <span className={styles.copy_btn}>
                            {t('order.orderDetail.common.copyText')}
                          </span>
                        </CopyToClipboard>
                      </div>
                    ) : (
                      <div className={styles.errorText}>
                        {t('order.orderDetail.trackModal.noDataText')}
                      </div>
                    )}
                  </li>
                ) : null}

                {promiseValue ? (
                  <li className={styles.express_item}>
                    <span>{promiseTitle}</span>
                    <span>{promiseValue}</span>
                  </li>
                ) : null}
                {selectedPromiseValue ? (
                  <li className={styles.express_item}>
                    <span>{selectedPromiseTitle}</span>
                    <span>{selectedPromiseValue}</span>
                  </li>
                ) : null}
                {(isSelfPickupOrder && selfPickupAddress) || (!isSelfPickupOrder && address) ? (
                  <li className={cn(styles.express_item, styles.express_item_special)}>
                    <span className={styles.addressTitleStyle}>{addressTitle}</span>
                    <div className={styles.address_item}>
                      <div className={styles.address_item_inner}>
                        {/* <span className={styles.address_nick_name}>{customerName}</span> */}
                        {/* <span className={styles.address_postcode}>{mobile}</span> */}
                        <UKTruncatedTooltip
                          text={(isSelfPickupOrder ? selfPickupName : customerName) as string}
                          tooltipStyles={styles.address_nick_name}
                          tooltipProps={{
                            placement: 'top',
                            arrow: true,
                          }}
                        />
                        {!isSelfPickupOrder && ( // pickup订单不展示联系方式
                          <UKTruncatedTooltip
                            text={mobile as string}
                            tooltipStyles={styles.address_postcode}
                            tooltipProps={{
                              placement: 'top',
                              arrow: true,
                            }}
                          />
                        )}
                      </div>
                      {/* <div className={styles.address_detail}>{address}</div> */}
                      <UKTruncatedTooltip
                        text={(isSelfPickupOrder ? selfPickupAddress : address) as string}
                        tooltipStyles={styles.address_detail}
                        tooltipProps={{
                          placement: 'top',
                          arrow: true,
                        }}
                      />
                    </div>
                  </li>
                ) : null}
              </ul>

              {/* {logisticsInfoDTOList?.length ? ( */}
              <div className={styles.logistics_content}>
                <Stepper logisticsInfoDTOList={packageLogistics || []} />
              </div>
              {/* ) : null} */}
            </div>
          )}
        </DialogContent>
      </UKDialog>
    );
  },
);

export default trackModal;
